import WebApp from '@twa-dev/sdk';
import { useShallow } from 'zustand/react/shallow';

import { getGamesByCategory, getGenres, getSelections } from '@/shared/api/api';
import { GAME_WRAPPER_APP_LINK } from '@/shared/constants';
import { base64Utils } from '@/shared/helpers/base64-utils';
import { createClearableStore } from '@/shared/helpers/create-clearable-store';
import { makePageableResult } from '@/shared/helpers/make-pageable-result';
import { parseJSON } from '@/shared/helpers/parse-json';
import { playClickEvent } from '@/shared/services/analytics/events/play-click-event';

import { type PageableRes } from '../../types/common';
import { useAppStateRaw } from '../app-store/app-store';
import { useServiceStateRaw } from '../services-store/services-store';
import type {
  CategoryGames,
  Game,
  GamesState,
  GenreLocalization,
  LastFetchedPages,
  Selection,
} from './types';

const defaultGamesStore = {
  categoryGames: null,
  lastFetchedPages: {} as LastFetchedPages,
  selections: undefined,
  selectionsByKey: undefined,
  genres: undefined,
};

export const useGamesStoreRaw = createClearableStore<GamesState>()((set, get) => ({
  ...defaultGamesStore,
  actions: {
    hydrate: (fresh: Partial<GamesState>) => set(fresh),
    fetchGenres: async () => {
      try {
        const rawGenres = await getGenres();
        const genres = rawGenres.map((g) => ({
          ...g,
          name: parseJSON<GenreLocalization>(g.name),
          nameAccusative: parseJSON<GenreLocalization>(g.nameAccusative),
        }));

        set({ genres });
      } catch (err) {
        console.error('fetch selections ERROR', err);
      }
    },
    fetchSelections: async () => {
      try {
        const selections = (await getSelections()).sort((a, b) => a.n - b.n);

        const selectionsByKey: Record<string, Selection> = {};

        if (selections.length) {
          for (let index = 0; index < selections.length; index++) {
            const selection = selections[index];

            selectionsByKey[selection.selectionKey] = selection;
          }
        }

        set({ selections, selectionsByKey });
      } catch (err) {
        console.error('fetch selections ERROR', err);
      }
    },
    fetchGamesByCategory: async (page, category, size) => {
      try {
        size ??= category === 'new' ? 15 : 10;
        const res = await getGamesByCategory<PageableRes<Game[]>>(category, page, size);

        const { content, pageable } = res;

        const pages = get().lastFetchedPages;
        set({ lastFetchedPages: { ...pages, [category]: page } });

        set((prev) => {
          if (!prev.categoryGames) prev.categoryGames = {} as CategoryGames;

          return {
            categoryGames: {
              ...prev.categoryGames,
              [category]: makePageableResult(prev.categoryGames[category], {
                content,
                pageable,
              }),
            },
          };
        });

        return res;
      } catch (err) {
        console.error('fetchGamesByCategory ERROR', err);
        set({ categoryGames: undefined });
        throw err;
      }
    },
    playClick: async (
      game,
      placement,
      analyticsService,
      placementNum,
      impressionContext
    ) => {
      const event = playClickEvent({
        event_properties: {
          game_key: game.key,
          name: 'game_play',
          placement,
          placement_num: placementNum,
          source: placement,
          is_adult: game.flags?.adult ?? false,
          ...(impressionContext && {
            impression_batch_id: impressionContext.feedScrollPage,
            visible_game_keys: impressionContext.visibleGameKeys,
          }),
        },
      });

      analyticsService.send(event);

      try {
        if (game.flags.bot) {
          window.open(game.gameUrl);
          return;
        }

        if (game.flags.webapp) {
          const startapp = base64Utils.encode({
            src: 'from_market',
            game: { key: game.key },
            locale: useAppStateRaw.getState().lang,
          });

          const resolvedWrapperUrl = `${GAME_WRAPPER_APP_LINK}?startapp=${startapp}`;

          WebApp?.disableClosingConfirmation();

          WebApp.openTelegramLink(resolvedWrapperUrl);
          return;
        }

        WebApp.showPopup({
          title: 'Something went wrong.',
          message: 'Incorrect launch flag. Check webapp or bot flag',
        });
      } catch (err) {
        const { loggerService } = useServiceStateRaw.getState();
        loggerService.logError(err as Error, 'gamesStore: playClick function');
      }
    },
  },
}));

export const useGamesStore = () =>
  useGamesStoreRaw(
    useShallow((state) => ({
      selections: state.selections,
      genres: state.genres,
      categoryGames: state.categoryGames,
      lastFetchedPages: state.lastFetchedPages,
    }))
  );

export const useGenres = () => useGamesStoreRaw(useShallow((state) => state.genres));

export const useSelectionByKey = (selectionKey: string) =>
  useGamesStoreRaw(useShallow((state) => state.selectionsByKey?.[selectionKey]));

export const useGamesByCategory = (selectionKey: string) =>
  useGamesStoreRaw(useShallow((state) => state.categoryGames?.[selectionKey]));

export const useGamesActions = () =>
  useGamesStoreRaw(useShallow((state) => state.actions));
